import { useState, useEffect, useCallback } from 'react';
import { storageManager, SaveResult } from '../utils/storageManager';
import { 
  HeightClearanceMap, 
  CalculationResults 
} from '../utils/calculationUtils';
import { requiresBallastedTrackHandling } from '../utils/gorUtils';
import { DIVISION_LIMITS } from '../utils/constants';

// Define input parameters for calculation validation
interface CalculationValidationParams {
  H: number | null;
  D: number | null;
  MO: number | null;
  SUPER: number | null;
  trackType: 'curve' | 'tangent' | string;
  isSpecialGorRange?: boolean;
}

// Comprehensive validation state
interface ValidationState {
  torGorError: {
    hasError: boolean;
    message: string;
  };
  missingFields: string[];
  invalidFields: Set<string>;
  missingFieldsForCalculate: Set<string>; // Only set when Calculate button pressed
  fieldShakes: Set<string>;
  triggerFieldShake: (fieldName: string) => void;
  clearFieldValidation: (fieldName: string) => void;
  clearCalculateFieldError: (fieldName: string) => void;
  clearValidationOnInput: (fieldName: string, fieldValue: number | null) => void;
  clearValidationOnChange: (fieldName: string, inputValue: string | number | null | undefined) => void;
  clearAllValidation: () => void;
  showCalculateErrors: (missingFields: string[], hasTorGorError: boolean) => void;
}

// Validation hook parameters
interface ValidationHookParams {
  H: number | null;
  D: number | null;
  MO: number | null;
  SUPER: number | null;
  division: 'A Division' | 'B Division';
  trackType: 'curve' | 'tangent';
  isSpecialGorRange: boolean;
  hasStartedInput: boolean;
}

// Define types for calculation inputs
interface CalculationInputs {
  division: string;
  trackType: string;
  direction: string;
  H: number | string;
  D: number | string;
  MO?: number | string;
  SUPER: number | string;
  [key: string]: any; // Allow for additional properties
}

export interface SavedCalculation {
  id: number;
  name: string;
  timestamp: string;
  track?: string;
  inputs: CalculationInputs;
  results: CalculationResults;
  ballastedTrack?: boolean;
  verticalCurvature?: boolean;
}

interface StorageWarning {
  show: boolean;
  message: string;
}

/**
 * Hook to manage the translation data loading
 * @returns Translation maps for both divisions
 */
export const useTranslationData = (): { 
  A_height_to_clearance: HeightClearanceMap; 
  B_height_to_clearance: HeightClearanceMap;
  A_above_gor_to_height: HeightClearanceMap;
  B_above_gor_to_height: HeightClearanceMap;
} => {
  const [A_height_to_clearance, setATranslate] = useState<HeightClearanceMap>({0: 33.875});
  const [B_height_to_clearance, setBTranslate] = useState<HeightClearanceMap>({0: 35.125});
  const [A_above_gor_to_height, setAAboveTranslate] = useState<HeightClearanceMap>({0: 142.3125});
  const [B_above_gor_to_height, setBAboveTranslate] = useState<HeightClearanceMap>({0: 145.875});

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        const response = await fetch('/translations.json'); 
        if (!response.ok) {
          throw new Error(`Failed to fetch translations.json: ${response.status}`);
        }
        const jsonData = await response.json();

        // Extract the mappings with defaults
        const divA_height_to_clearance = jsonData.A_Division || {};
        const divB_height_to_clearance = jsonData.B_Division || {};
        const divA_above_gor_to_height = jsonData.A_Division_Above || {};
        const divB_above_gor_to_height = jsonData.B_Division_Above || {};

        // Set the state with the mappings
        setATranslate(divA_height_to_clearance);
        setBTranslate(divB_height_to_clearance);
        setAAboveTranslate(divA_above_gor_to_height);
        setBAboveTranslate(divB_above_gor_to_height);
      } catch (error) {
        console.error('Error fetching or parsing translations.json:', error);
        // Keep default values if fetching fails
      }
    };
    
    fetchData();
  }, []);

  return { A_height_to_clearance, B_height_to_clearance, A_above_gor_to_height, B_above_gor_to_height };
};

/**
 * Comprehensive validation hook for input validation with enhanced field-specific controls
 */
export const useInputValidation = ({ 
  H, D, MO, SUPER, division, trackType, isSpecialGorRange, hasStartedInput
}: ValidationHookParams): ValidationState => {
  const [validationState, setValidationState] = useState<ValidationState>(() => ({
    torGorError: { hasError: false, message: '' },
    missingFields: [],
    invalidFields: new Set(),
    missingFieldsForCalculate: new Set(),
    fieldShakes: new Set(),
    triggerFieldShake: () => {},
    clearFieldValidation: () => {},
    clearCalculateFieldError: () => {},
    clearValidationOnInput: () => {},
    clearValidationOnChange: () => {},
    clearAllValidation: () => {},
    showCalculateErrors: () => {}
  }));

  const triggerFieldShake = (fieldName: string) => {
    setValidationState(prev => ({
      ...prev,
      fieldShakes: new Set([...prev.fieldShakes, fieldName])
    }));
    
    // Clear shake after animation
    setTimeout(() => {
      setValidationState(prev => ({
        ...prev,
        fieldShakes: new Set([...prev.fieldShakes].filter(field => field !== fieldName))
      }));
    }, 600);
  };

  const clearFieldValidation = (fieldName: string) => {
    setValidationState(prev => {
      const newInvalidFields = new Set([...prev.invalidFields].filter(field => field !== fieldName));
      
      // Check if this was part of TOR/GOR error - clear it if both fields are now valid
      const newTorGorError = prev.torGorError.hasError && (fieldName === 'H' || fieldName === 'D') && 
        !newInvalidFields.has('H') && !newInvalidFields.has('D')
        ? { hasError: false, message: '' }
        : prev.torGorError;
      
      return {
        ...prev,
        invalidFields: newInvalidFields,
        torGorError: newTorGorError
      };
    });
  };

  const clearCalculateFieldError = (fieldName: string) => {
    setValidationState(prev => ({
      ...prev,
      missingFieldsForCalculate: new Set([...prev.missingFieldsForCalculate].filter(field => field !== fieldName))
    }));
  };

  // New method for immediate validation clearing when field gets valid content
  const clearValidationOnInput = (fieldName: string, fieldValue: number | null) => {
    // Clear validation immediately if field has valid content
    if (fieldValue !== null && !isNaN(fieldValue)) {
      setValidationState(prev => ({
        ...prev,
        missingFieldsForCalculate: new Set([...prev.missingFieldsForCalculate].filter(field => field !== fieldName))
      }));
    }
  };

  // Enhanced method for immediate validation clearing based on input event value
  const clearValidationOnChange = (fieldName: string, inputValue: string | number | null | undefined) => {
    // Parse the input value to check if it's valid
    const isValidInput = inputValue !== null && 
                        inputValue !== undefined && 
                        inputValue !== '' && 
                        !isNaN(Number(inputValue));
    
    if (isValidInput) {
      setValidationState(prev => ({
        ...prev,
        missingFieldsForCalculate: new Set([...prev.missingFieldsForCalculate].filter(field => field !== fieldName))
      }));
    }
  };

  const clearAllValidation = () => {
    setValidationState(prev => ({
      ...prev,
      torGorError: { hasError: false, message: '' },
      invalidFields: new Set(),
      missingFieldsForCalculate: new Set(),
      fieldShakes: new Set()
    }));
  };

  const showCalculateErrors = (missingFields: string[], hasTorGorError: boolean) => {
    const fieldsToValidate = new Set<string>();
    const fieldsToShake = new Set<string>();
    
    // Add missing fields to calculate errors
    if (H === null) fieldsToValidate.add('H');
    if (D === null) fieldsToValidate.add('D');
    if (!isSpecialGorRange) {
      if (trackType === 'curve' && MO === null) fieldsToValidate.add('MO');
      if (SUPER === null) fieldsToValidate.add('SUPER');
    }
    
    // Add all invalid fields to shake
    fieldsToValidate.forEach(field => fieldsToShake.add(field));
    if (hasTorGorError) {
      fieldsToShake.add('H');
      fieldsToShake.add('D');
    }
    
    setValidationState(prev => ({
      ...prev,
      missingFieldsForCalculate: fieldsToValidate,
      fieldShakes: fieldsToShake
    }));
    
    // Clear shakes after animation
    setTimeout(() => {
      setValidationState(prev => ({
        ...prev,
        fieldShakes: new Set()
      }));
    }, 600);
  };

  useEffect(() => {
    const newInvalidFields = new Set<string>();
    let torGorError = { hasError: false, message: '' };
    const missingFields: string[] = [];

    // TOR/GOR validation (ONLY if both H and D have values)
    if (H !== null && D !== null) {
      // Only validate wayside threshold when GOR > 0
      if (D > 0) {
        const threshold = division === 'A Division' ? 
          DIVISION_LIMITS.A_WAYSIDE_THRESHOLD : 
          DIVISION_LIMITS.B_WAYSIDE_THRESHOLD;
        
        if (H >= threshold) {
          torGorError = {
            hasError: true,
            message: `Wayside measurements have a maximum height of ${threshold} inches.`
          };
          newInvalidFields.add('H');
          newInvalidFields.add('D');
        }
      }
    }

    // For immediate validation, also check if user has started input
    // This ensures TOR/GOR validation appears as soon as both fields have values
    if (hasStartedInput && H !== null && D !== null && D > 0) {
      const threshold = division === 'A Division' ? 
        DIVISION_LIMITS.A_WAYSIDE_THRESHOLD : 
        DIVISION_LIMITS.B_WAYSIDE_THRESHOLD;
      
      if (H >= threshold) {
        torGorError = {
          hasError: true,
          message: `Wayside measurements have a maximum height of ${threshold} inches.`
        };
        newInvalidFields.add('H');
        newInvalidFields.add('D');
      }
    }

    // Missing field tracking for information only (not for validation display)
    if (H === null) missingFields.push('Height from T.O.R.');
    if (D === null) missingFields.push('Distance from G.O.R.');
    if (!isSpecialGorRange) {
      if (trackType === 'curve' && MO === null) missingFields.push('Middle Ordinate');
      if (SUPER === null) missingFields.push('Super Elevation');
    }

    setValidationState(prev => ({
      ...prev,
      torGorError,
      missingFields,
      invalidFields: newInvalidFields,
      triggerFieldShake,
      clearFieldValidation,
      clearCalculateFieldError,
      clearValidationOnInput,
      clearValidationOnChange,
      clearAllValidation,
      showCalculateErrors
    }));
  }, [H, D, MO, SUPER, division, trackType, isSpecialGorRange]);

  return validationState;
};

/**
 * Hook to manage calculation validation
 * @param params - Input parameters
 * @returns Whether calculation can be performed
 */
export const useCalculationValidation = ({ 
  H, D, MO, SUPER, trackType, isSpecialGorRange = false
}: CalculationValidationParams): boolean => {
  const [calculateEnabled, setCalculateEnabled] = useState<boolean>(false);

  useEffect(() => {
    const isValidNumber = (value: number | string | null): boolean => {
      return value !== null && typeof value === 'number' && !isNaN(value);
    };

    const canCalculate = (): void => {
      // Special case: When in the special GOR range (0 to -28.25), only H and D are required
      if (isSpecialGorRange) {
        if (isValidNumber(H) && isValidNumber(D)) {
          setCalculateEnabled(true);
        } else {
          setCalculateEnabled(false);
        }
      }
      // Standard cases
      else if (trackType === 'curve') {
        if (isValidNumber(H) && isValidNumber(D) && isValidNumber(MO) && isValidNumber(SUPER)) {
          setCalculateEnabled(true);
        } else {
          setCalculateEnabled(false);
        }
      } else if (trackType === 'tangent') {
        if (isValidNumber(H) && isValidNumber(D) && isValidNumber(SUPER)) {
          setCalculateEnabled(true);
        } else {
          setCalculateEnabled(false);
        }
      } else {
        setCalculateEnabled(false);
      }
    };

    canCalculate();
  }, [H, D, MO, SUPER, trackType, isSpecialGorRange]);

  return calculateEnabled;
};

/**
 * Hook to manage saved calculations and storage
 * @returns Saved calculations state and handlers
 */
export const useSavedCalculations = (): {
  savedCalculations: SavedCalculation[];
  setSavedCalculations: React.Dispatch<React.SetStateAction<SavedCalculation[]>>;
  storageWarning: StorageWarning | null;
  setStorageWarning: React.Dispatch<React.SetStateAction<StorageWarning | null>>;
  saveCalculation: (inputs: CalculationInputs, results: CalculationResults, saveName?: string, track?: string, ballastedTrack?: boolean, verticalCurvature?: boolean) => Promise<SaveResult>;
  loadSavedCalculations: () => Promise<void>;
} => {
  const [savedCalculations, setSavedCalculations] = useState<SavedCalculation[]>([]);
  const [storageWarning, setStorageWarning] = useState<StorageWarning | null>(null);

  // Function to load saved calculations from storage
  const loadSavedCalculations = useCallback(async (): Promise<void> => {
    try {
      // Check storage status and load calculations in parallel
      const [isLow, storedCalculations] = await Promise.all([
        storageManager.isStorageLow(),
        Promise.resolve(localStorage.getItem('savedCalculations'))
      ]);
      
      if (isLow) {
        setStorageWarning({
          show: true,
          message: "Storage space is running low. Consider exporting and deleting older calculations."
        });
      }

      if (storedCalculations) {
        setSavedCalculations(JSON.parse(storedCalculations) as SavedCalculation[]);
      }
    } catch (error) {
      console.error("Error loading saved calculations:", error);
    }
  }, []);

  // Load saved calculations on mount
  useEffect(() => {
    loadSavedCalculations();
  }, []);

  // Listen for storage updates
  useEffect(() => {
    const handleStorageUpdate = () => loadSavedCalculations();
    
    window.addEventListener('storageUpdate', handleStorageUpdate);
    
    return () => {
      window.removeEventListener('storageUpdate', handleStorageUpdate);
    };
  }, [loadSavedCalculations]);

  // Function to save a calculation
  const saveCalculation = async (
    inputs: CalculationInputs, 
    results: CalculationResults, 
    saveName?: string,
    track?: string,
    ballastedTrack?: boolean,
    verticalCurvature?: boolean
  ): Promise<SaveResult> => {
    // Create calculation object
    const newCalculation: SavedCalculation = {
      id: Date.now(),
      name: saveName || `Calculation ${savedCalculations.length + 1}`,
      timestamp: new Date().toISOString(),
      track: track || undefined,
      inputs,
      results
    };

    // Add ballasted track and vertical curvature data only for GOR -28.25
    if (requiresBallastedTrackHandling(Number(inputs.D))) {
      newCalculation.ballastedTrack = ballastedTrack;
      newCalculation.verticalCurvature = verticalCurvature;
    }

    const updatedCalculations = [...savedCalculations, newCalculation];

    // Check if we can save this safely
    const saveResult = await storageManager.saveCalculations(updatedCalculations);

    if (saveResult.success) {
      setSavedCalculations(updatedCalculations);
      return { success: true };
    } else {
      // Handle storage limit issue
      setStorageWarning({
        show: true,
        message: "Storage space is full. Please export and delete some calculations."
      });

      // Try to free up space
      const cleanupResult = await storageManager.performStorageCleanup();
      if (cleanupResult.success) {
        // Retry save after cleanup
        const retrySaveResult = await storageManager.saveCalculations([
          ...savedCalculations.slice(cleanupResult.removedCount),
          newCalculation
        ]);

        if (retrySaveResult.success) {
          setSavedCalculations([
            ...savedCalculations.slice(cleanupResult.removedCount),
            newCalculation
          ]);
          setStorageWarning({
            show: true,
            message: `Storage cleanup removed ${cleanupResult.removedCount} old calculations to free up space.`
          });
          return { success: true };
        }
      }
      
      return { success: false };
    }
  };

  return { 
    savedCalculations, 
    setSavedCalculations, 
    storageWarning, 
    setStorageWarning, 
    saveCalculation,
    loadSavedCalculations
  };
};